// RadioPage.jsx
import { useState, useEffect } from 'react';
import { radioAPI } from '../../utils/api';
import PublicLayout from '../../components/layout/PublicLayout';
import ReactPlayer from 'react-player';
import useSEO, { PAGE_SEO } from '../../hooks/useSEO';
import LogoImage from '../../components/ui/LogoImage';

export function RadioPage() {
  useSEO(PAGE_SEO.radio);
  const [radio, setRadio]     = useState(null);
  const [playing, setPlaying] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    radioAPI.getConfig().then(r => setRadio(r.data.data)).finally(() => setLoading(false));
  }, []);

  return (
    <PublicLayout>
      <div className="min-h-[70vh] flex items-center justify-center pattern-bg py-16">
        <div className="max-w-2xl w-full mx-auto px-4">
          <div className="bg-navy-800 rounded-3xl shadow-2xl overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-navy-700 to-navy-900 px-8 py-10 text-center">
              <div className="flex items-center justify-center gap-3 mb-3">
                {radio?.is_live && <span className="radio-live-dot h-3 w-3 rounded-full bg-red-400 inline-block" />}
                <h1 className="text-white text-2xl font-bold">{radio?.station_name || 'JNF Radio'}</h1>
              </div>
              <p className="text-white/60 text-sm">{radio?.description || 'Live Islamic broadcasts'}</p>
            </div>

            {/* Player */}
            <div className="px-8 py-8 text-center">
              {loading ? (
                <div className="h-24 flex items-center justify-center">
                  <div className="animate-spin h-10 w-10 rounded-full border-4 border-cyan-400 border-t-transparent" />
                </div>
              ) : radio?.stream_url ? (
                <>
                  <div className="hidden">
                    <ReactPlayer url={radio.stream_url} playing={playing} onError={() => setPlaying(false)} />
                  </div>
                  <button
                    onClick={() => setPlaying(!playing)}
                    className={`h-20 w-20 mx-auto rounded-full flex items-center justify-center text-3xl transition-all mb-4 shadow-lg ${playing ? 'bg-red-500 hover:bg-red-600 scale-110' : 'bg-cyan-400 hover:bg-cyan-300'}`}
                  >
                    {playing ? '⏸' : '▶'}
                  </button>
                  <p className="text-white/60 text-sm">{playing ? '🔊 Now Playing...' : 'Click to tune in'}</p>
                  {playing && (
                    <div className="flex justify-center gap-1.5 mt-4">
                      {[...Array(5)].map((_, i) => (
                        <div key={i} className="w-1 bg-cyan-400 rounded-full animate-pulse" style={{ height: `${12 + (i % 3) * 8}px`, animationDelay: `${i * 0.15}s` }} />
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <p className="text-white/40 py-8">Radio stream not available at this time.</p>
              )}

              {radio?.schedule_info && (
                <div className="mt-6 bg-white/5 rounded-xl p-4 text-left">
                  <p className="text-cyan-400 text-xs font-semibold uppercase tracking-wider mb-2">Schedule</p>
                  <p className="text-white/70 text-sm whitespace-pre-line">{radio.schedule_info}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </PublicLayout>
  );
}

// ─────────────────────────────────────────────────────────────────
// SawiaPage  — radio-station style layout
// ─────────────────────────────────────────────────────────────────
import { sawiaAPI } from '../../utils/api';
import { MapPinIcon, PhoneIcon, EnvelopeIcon } from '@heroicons/react/24/outline';

export function SawiaPage() {
  useSEO(PAGE_SEO.sawia);
  const [locations, setLocations] = useState([]);
  const [activeState, setActiveState] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    sawiaAPI.getAll().then(r => {
      const locs = r.data.data || [];
      setLocations(locs);
      // default to first state that has results
      if (locs.length) {
        const first = locs[0].state || locs[0].country || '';
        setActiveState(first);
      }
    }).finally(() => setLoading(false));
  }, []);

  // Build unique state/region tabs with counts
  const stateCounts = locations.reduce((acc, loc) => {
    const key = loc.state || loc.country || 'Other';
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});
  const stateKeys = Object.keys(stateCounts);

  // Filter by active state
  const filtered = activeState
    ? locations.filter(l => (l.state || l.country || 'Other') === activeState)
    : locations;

  return (
    <PublicLayout>
      <div className="max-w-7xl mx-auto px-4 py-12">

        {/* Page header */}
        <div className="mb-8">
          <h1 className="section-title mb-2">Sawia Locations</h1>
          <p className="text-gray-500">Find a Zawiya (Sawia) near you across our branches</p>
        </div>

        {loading ? (
          <div className="space-y-4">
            <div className="flex gap-2">{[1,2,3].map(i => <div key={i} className="h-10 w-32 bg-gray-100 rounded-full animate-pulse" />)}</div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
              {[1,2,3,4,5,6].map(i => <div key={i} className="h-52 bg-gray-100 rounded-2xl animate-pulse" />)}
            </div>
          </div>
        ) : locations.length === 0 ? (
          <div className="text-center py-20 text-gray-400">
            <MapPinIcon className="h-12 w-12 mx-auto mb-4 text-gray-200" />
            <p className="font-medium">No Sawia locations added yet.</p>
          </div>
        ) : (
          <>
            {/* ── Region / State tabs ── */}
            {stateKeys.length > 1 && (
              <div className="flex gap-2 flex-wrap mb-8">
                {stateKeys.map(state => (
                  <button
                    key={state}
                    onClick={() => setActiveState(state)}
                    className={`px-5 py-2 rounded-full text-sm font-semibold border-2 transition-all ${
                      activeState === state
                        ? 'bg-navy-800 text-white border-navy-800'
                        : 'bg-white text-navy-700 border-gray-200 hover:border-navy-300'
                    }`}
                  >
                    {state} ({stateCounts[state]})
                  </button>
                ))}
              </div>
            )}

            {/* ── Cards grid ── */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {filtered.map(loc => (
                <SawiaCard key={loc.id} loc={loc} />
              ))}
            </div>
          </>
        )}
      </div>
    </PublicLayout>
  );
}

function SawiaCard({ loc }) {
  return (
    <div className="rounded-2xl overflow-hidden shadow-sm border border-gray-100 hover:shadow-md transition-shadow">

      {/* ── Dark navy header ── */}
      <div className="bg-gradient-to-br from-navy-800 to-navy-950 px-5 py-5">
        <div className="flex items-start justify-between gap-3">
          {/* Name */}
          <div>
            <h3 className="text-white font-bold text-lg leading-tight">{loc.name}</h3>
            {loc.name_ar && (
              <p className="arabic text-cyan-300 text-base mt-0.5">{loc.name_ar}</p>
            )}
          </div>
          {/* City + State pin */}
          {(loc.city || loc.state) && (
            <div className="flex items-start gap-1 flex-shrink-0 text-right">
              <MapPinIcon className="h-3.5 w-3.5 text-red-400 mt-0.5 flex-shrink-0" />
              <div>
                {loc.city  && <p className="text-white/90 text-xs font-medium">{loc.city}</p>}
                {loc.state && <p className="text-white/60 text-xs">{loc.state}</p>}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── White body ── */}
      <div className="bg-white px-5 py-4 space-y-3">

        {/* Description */}
        {loc.description && (
          <p className="text-gray-500 text-sm leading-relaxed line-clamp-2">{loc.description}</p>
        )}

        {/* Address block — styled like the schedule box in radio cards */}
        {loc.address && (
          <div className="bg-gray-50 rounded-xl px-4 py-3">
            <div className="flex items-start gap-3">
              <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm w-full">
                <span className="text-gray-400 text-xs font-medium pt-0.5">Address</span>
                <span className="text-navy-800 font-semibold text-xs">
                  {[loc.address, loc.city, loc.state].filter(Boolean).join(', ')}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Contact row */}
        {(loc.phone || loc.email) && (
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            {loc.phone && (
              <a href={`tel:${loc.phone}`} className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-cyan-600 transition-colors">
                <PhoneIcon className="h-3.5 w-3.5 text-cyan-400" />
                {loc.phone}
              </a>
            )}
            {loc.email && (
              <a href={`mailto:${loc.email}`} className="flex items-center gap-1.5 text-xs text-cyan-600 hover:underline">
                <EnvelopeIcon className="h-3.5 w-3.5 text-cyan-400" />
                {loc.email}
              </a>
            )}
          </div>
        )}

        {/* Google Maps link */}
        {loc.latitude && loc.longitude && (
          <a
            href={`https://maps.google.com/?q=${loc.latitude},${loc.longitude}`}
            target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-navy-600 hover:text-cyan-600 transition-colors mt-1"
          >
            📍 Open in Google Maps
          </a>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// QuotesPage
// ─────────────────────────────────────────────────────────────────
import { quotesAPI } from '../../utils/api';

export function QuotesPage() {
  useSEO(PAGE_SEO.quotes);
  const [quotes, setQuotes]   = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    quotesAPI.getAll().then(r => setQuotes(r.data.data || [])).finally(() => setLoading(false));
  }, []);

  return (
    <PublicLayout>
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="text-center mb-10">
          <p className="text-cyan-500 text-xs font-semibold uppercase tracking-widest mb-2">✦ Pearls of Wisdom ✦</p>
          <h1 className="section-title">Murshid Quotes</h1>
        </div>
        {loading ? (
          <div className="space-y-6">{[1,2,3].map(i => <div key={i} className="h-52 bg-gray-100 rounded-2xl animate-pulse" />)}</div>
        ) : (
          <div className="space-y-6">
            {quotes.map(q => (
              <div key={q.id} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 hover:shadow-md transition-shadow relative overflow-hidden">
                {/* Decorative motif */}
                <div className="absolute top-4 right-6 text-6xl text-navy-100 select-none pointer-events-none" aria-hidden>❝</div>
                {q.is_featured && <span className="badge bg-cyan-100 text-cyan-700 mb-4 inline-block text-xs">⭐ Featured</span>}
                {/* English translation as primary content */}
                <p className="text-navy-800 text-lg md:text-xl leading-relaxed mb-5 font-medium relative z-10">
                  {q.translation || q.arabic_text || 'No content available.'}
                </p>
                <div className="flex items-center justify-between flex-wrap gap-2 pt-4 border-t border-gray-100">
                  <p className="text-cyan-600 font-semibold text-sm">— {q.murshid_name}</p>
                  {q.source && <p className="text-gray-400 text-xs">{q.source}</p>}
                </div>
              </div>
            ))}
            {quotes.length === 0 && <p className="text-center text-gray-400 py-16">No quotes available yet.</p>}
          </div>
        )}
      </div>
    </PublicLayout>
  );
}

// ─────────────────────────────────────────────────────────────────
// AboutPage
// ─────────────────────────────────────────────────────────────────
export function AboutPage() {
  useSEO(PAGE_SEO.about);
  return (
    <PublicLayout>
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="text-center mb-12">
          <div className="flex justify-center mb-6">
            <LogoImage size={96} className="rounded-full border-4 border-cyan-400 shadow-lg" />
          </div>
          <h1 className="section-title mb-3">About JNF</h1>
          <p className="text-gray-500 max-w-2xl mx-auto">Jamuiyat Nasir Al-Fridaous Al Tijaniyyah</p>
        </div>

        <div className="prose max-w-none space-y-6 text-gray-700">
          <div className="bg-navy-50 border-l-4 border-cyan-400 p-6 rounded-r-2xl">
            <p className="arabic text-xl text-navy-800 text-center leading-loose">كفى بالله وليا وكفى بالله نصيرا</p>
            <p className="text-center text-gray-500 text-sm mt-2 italic">"Sufficient is Allah as a Protector, and sufficient is Allah as a Helper"</p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {[
              { title: 'Our Foundation', body: 'Jamuiyat Nasir Al-Fridaous Al Tijaniyyah is a Sufi Islamic JAMA following the noble Tijaniyyah Tariqa, established by Shaykh Ahmad ibn Muhammad al-Tijani (R.A).' },
              { title: 'Our Mission', body: 'To spread the love of Allah and His Prophet ﷺ through the practice of daily Azkar, regular Wazifah, and communal gatherings in remembrance of Allah.' },
              { title: 'The Tijaniyyah Way', body: 'We follow the path of Shaykh Ibrahim Niasse (R.A), the Fayda of Tijaniyyah — a spiritual flood of divine mercy that has transformed millions of lives across West Africa and the world.' },
              { title: 'Community', body: 'We maintain Sawia (Zawiyas) across multiple locations, providing spaces for worship, learning, and brotherhood for all seekers on the path.' },
            ].map(({ title, body }) => (
              <div key={title} className="card p-6">
                <h3 className="font-bold text-navy-700 mb-3 text-lg">{title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </PublicLayout>
  );
}
